import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Skeleton, Table, Tag, Input, Select, Button } from "antd";
import { useNavigate } from "react-router-dom";
import ManagerLayout from "../Layouts/ManagerLayout";

const { Search } = Input;
const { Option } = Select;

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ManagerTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(""); // State for search input
  const [dateFilter, setDateFilter] = useState("all"); // State for date filter
  const [serviceFilter, setServiceFilter] = useState([]); // State for service column filter
  const [statusFilter, setStatusFilter] = useState([]); // State for status column filter
  const [currentPage, setCurrentPage] = useState(1); // State for current page
  const [pageSize, setPageSize] = useState(50); // State for page size
  const [total, setTotal] = useState(0); // Total number of tickets
  const navigate = useNavigate();

  const fetchTickets = useCallback(
    async (page, size, search, date, services, statuses) => {
      const manager = localStorage.getItem("name");
      const token = localStorage.getItem("token");

      if (!manager || !token) {
        console.error("Missing user ID or token in localStorage.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${backendUrl}/manager/getmanagerticket`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              manager,
              page,
              limit: size,
              search: search || undefined,
              dateFilter: date,
              service: services.length ? services.join(",") : undefined,
              status: statuses.length ? statuses.join(",") : undefined,
            },
          }
        );

        setTickets(response.data.tickets || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        console.error(
          "Error fetching tickets:",
          error.response?.data?.message || error.message
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const prevSearchRef = useRef(searchText);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== searchText;
    prevSearchRef.current = searchText;

    // Debounce search typing, fetch immediately for every other change
    const timer = setTimeout(
      () => {
        fetchTickets(
          currentPage,
          pageSize,
          searchText,
          dateFilter,
          serviceFilter,
          statusFilter
        );
      },
      searchChanged && searchText.trim() !== "" ? 400 : 0
    );

    return () => clearTimeout(timer);
  }, [
    currentPage,
    pageSize,
    searchText,
    dateFilter,
    serviceFilter,
    statusFilter,
    fetchTickets,
  ]);

  // Reset to first page if the current page no longer exists (e.g. after a filter narrows results)
  useEffect(() => {
    const totalPages = Math.ceil(total / pageSize);
    if (total > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [total, currentPage, pageSize]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    setServiceFilter(filters.service || []);
    setStatusFilter(filters.status || []);
  };

  const columns = [
    {
      title: "Ticket ID",
      dataIndex: "ticketId",
      key: "ticketId",
      ellipsis: true,
      render: (text) => <span>TCK{text}</span>,
    },
    {
      title: "Enrollment",
      dataIndex: "enrollmentId",
      key: "enrollmentId",
      ellipsis: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      ellipsis: true,
      filters: [
        { text: "Amazon", value: "amazon" },
        { text: "Website", value: "website" },
        { text: "Dispatch", value: "dispatch" },
        { text: "Accounts", value: "accounts" },
      ],
      filteredValue: serviceFilter,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Open", value: "Open" },
        {
          text: "Waiting for manager reply",
          value: "Waiting for manager reply",
        },
        {
          text: "Waiting for customer reply",
          value: "Waiting for customer reply",
        },
      ],
      filteredValue: statusFilter,
      render: (status) => {
        const color =
          status === "Open" ? "green" : status === "Closed" ? "red" : "orange";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Assigned To",
      dataIndex: "assignee",
      key: "assignee",
      ellipsis: true,
      render: (assignee) => assignee || "Not Assigned",
    },
  ];

  const handleRowClick = (ticketId) => {
    navigate(`/managerticket/${ticketId}`);
  };

  return (
    <ManagerLayout>
      <div className="bg-gray-100 min-h-screen">
        <div className="w-full mb-4 pb-3 px-4 bg-gradient-to-r from-blue-800 to-blue-300 shadow-lg rounded-lg">
          <h1 className="text-2xl pt-4 font-bold text-white">All Tickets</h1>
          <div className="mt-4 flex items-center space-x-4">
            <Search
              placeholder="Search by Enrollment ID or Ticket ID"
              allowClear
              onChange={handleSearchChange} // Update on input change
              style={{ maxWidth: "400px" }}
            />
            <Select
              defaultValue="all"
              style={{ width: 200 }}
              onChange={handleDateFilterChange}
            >
              <Option value="all">All Dates</Option>
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="thisWeek">This Week</Option>
              <Option value="thisMonth">This Month</Option>
              <Option value="thisYear">This Year</Option>
            </Select>
            <Button>Total Tickets: {total}</Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex flex-wrap bg-white rounded-lg shadow-md p-4 space-x-4 lg:space-x-0"
              >
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
                <Skeleton.Input
                  active
                  className="w-full lg:w-1/6 mb-2 lg:mb-0"
                  style={{ height: "36px" }}
                />
              </div>
            ))}
          </div>
        ) : tickets.length > 0 ? (
          <div className="bg-white rounded-lg shadow-md">
            <div className="overflow-x-auto">
              <Table
                className="w-full cursor-pointer"
                columns={columns}
                dataSource={tickets}
                rowKey="ticketId"
                pagination={{
                  current: currentPage,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  showTotal: (t) => `Total ${t} tickets`,
                }}
                onChange={handleTableChange}
                bordered
                scroll={{ x: true }}
                onRow={(record) => ({
                  onClick: () => handleRowClick(record.ticketId),
                })}
              />
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">No tickets found.</p>
        )}
      </div>
    </ManagerLayout>
  );
};

export default ManagerTickets;
